// Fase 4: hook de RBAC visual.
//
// Este hook NÃO é fonte de autorização — o backend é, via AccessContext.
// `can(action)` só serve para esconder botões/menus que o backend de qualquer
// forma já bloquearia (com 403). Se a tabela de ações divergir do backend,
// a única consequência é o usuário ver um botão e receber 403 ao clicar.
// Nunca o contrário.
//
// Convenções:
//   - Ações `org.*` consultam `memberships` (owner/admin da org).
//     Espelham as rotas que o backend gating em SYNC_TRIGGER_ALLOWED_ROLES
//     (sync-today) e gestão de equipe (fase 5).
//   - Ações `lead.*`, `campaign.*`, `client.*` consultam o role do
//     `selectedClient` (vindo de `client_grants` no AccessContext).
//
// Fase 5: quando o scope é agregado (selectedClient === null e o usuário
// tem org/hybrid), o role efetivo passa a ser o MAIS PERMISSIVO entre os
// client_grants. Sem isso, ações como `lead.edit` ficariam escondidas
// permanentemente em scope agregado, mesmo para org admins.
//
// Ações desconhecidas retornam `false` — fail-closed.

import { useCallback, useMemo } from 'react'
import { useApp } from '../context/AppContext.jsx'

const ROLE_RANK = { admin: 3, editor: 2, viewer: 1 }
const ORG_FULL_ACCESS_ROLES = new Set(['owner', 'admin'])
// Issue #239: convites e gestão de usuários (org.invite / org.manage) passaram a
// valer para owner E admin — com limites aplicados no backend (admin não
// concede admin nem mexe no dono/outros admins). Nenhuma ação é só do owner.
const ORG_OWNER_ACTIONS = new Set()

const ACTION_MIN_ROLE = {
  // Org-level — verifica memberships
  'org.sync': 'admin',
  'org.invite': 'admin',
  'org.manage': 'admin',

  // Client-level — verifica role do selectedClient (ou agregado)
  'lead.view': 'viewer',
  'lead.edit': 'editor',
  'lead.delete': 'admin',
  'campaign.view': 'viewer',
  'campaign.edit': 'editor',
  'client.view': 'viewer',
  'client.invite': 'admin',
  'client.manage': 'admin',
}

const ACTION_MODULE = {
  'lead.view': 'crm',
  'lead.edit': 'crm',
  'lead.delete': 'crm',
  'campaign.view': 'ads',
  'campaign.edit': 'ads',
  'client.view': null,
  'client.invite': null,
  'client.manage': null,
}

function bestRole(grants) {
  if (!grants || grants.length === 0) return null
  return grants.reduce((best, g) => {
    const candidate = ROLE_RANK[g.role] ?? 0
    const current = ROLE_RANK[best] ?? 0
    return candidate > current ? g.role : best
  }, null)
}

function moduleRole(grant, module) {
  if (!grant || !module) return grant?.role || null
  const modules = grant.permissions?.modules
  if (!modules || Object.keys(modules).length === 0) return grant.role
  const raw = modules[module]
  if (raw === true) return grant.role
  if (!raw || raw === 'none') return null
  return raw
}

function bestModuleRole(grants, module) {
  if (!grants || grants.length === 0) return null
  return grants.reduce((best, grant) => {
    const candidateRole = moduleRole(grant, module)
    const candidate = ROLE_RANK[candidateRole] ?? 0
    const current = ROLE_RANK[best] ?? 0
    return candidate > current ? candidateRole : best
  }, null)
}

export function useAccess() {
  const ctx = useApp()
  const selectedClient = ctx?.selectedClient ?? ctx?.activeClient ?? null
  const memberships = ctx?.memberships || []
  const clientGrants = ctx?.clientGrants || []
  const accessMode = ctx?.accessMode || null

  const isOrgAdmin = useMemo(
    () => memberships.some((m) => ORG_FULL_ACCESS_ROLES.has(m.role)),
    [memberships]
  )
  const isOrgOwner = useMemo(
    () => memberships.some((m) => m.role === 'owner'),
    [memberships]
  )

  // Em scope agregado o role efetivo é o mais permissivo entre os grants.
  // Senão, é o role do cliente selecionado.
  const effectiveRole = useMemo(() => {
    if (selectedClient?.role) return selectedClient.role
    return bestRole(clientGrants)
  }, [selectedClient, clientGrants])

  const can = useCallback(
    (action) => {
      const minRole = ACTION_MIN_ROLE[action]
      if (!minRole) return false

      if (action.startsWith('org.')) {
        if (ORG_OWNER_ACTIONS.has(action)) return isOrgOwner
        return isOrgAdmin
      }

      const module = ACTION_MODULE[action]
      const scopedRole = selectedClient
        ? moduleRole(selectedClient, module)
        : module
          ? bestModuleRole(clientGrants, module)
          : effectiveRole

      if (!scopedRole) return false
      const userRank = ROLE_RANK[scopedRole] ?? 0
      const minRank = ROLE_RANK[minRole] ?? 0
      return userRank >= minRank
    },
    [clientGrants, effectiveRole, isOrgAdmin, isOrgOwner, selectedClient]
  )

  return {
    can,
    role: effectiveRole,
    via: selectedClient?.via || null,
    accessMode,
    isOrgAdmin,
    isOrgOwner,
    isClientOnly: accessMode === 'client_only',
    isAggregate:
      accessMode !== 'client_only' && selectedClient === null && clientGrants.length > 0,
    isReady: !!ctx,
  }
}
